import { Save, Check, Wand2, FileText, ImagePlus, Brush, Combine } from 'lucide-react';
import LlmModelPicker from './llm/LlmModelPicker';

interface PromptOptimizeForm {
  isEnabled: boolean;
  model: string;
  systemPromptText2img: string;
  systemPromptImg2img: string;
  systemPromptInpaint: string;
  mergePromptTemplate: string;
  temperature: number;
  maxTokens: number;
}

interface PromptOptimizeTabProps {
  form: PromptOptimizeForm;
  onFormChange: (form: PromptOptimizeForm) => void;
  onSave: () => void;
  saved: boolean;
}

export default function PromptOptimizeTab({ form, onFormChange, onSave, saved }: PromptOptimizeTabProps) {
  const setField = <K extends keyof PromptOptimizeForm>(field: K, value: PromptOptimizeForm[K]) => {
    onFormChange({ ...form, [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">AI提示词优化配置</h2>
          <p className="text-sm text-light-muted">配置AI大模型参数，用于优化用户输入的提示词</p>
        </div>
        <button
          onClick={onSave}
          className="bg-accent-blue text-white font-medium px-4 py-2 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex w-full items-center justify-center gap-2 sm:w-auto"
        >
          <Save className="w-4 h-4" />
          保存配置
        </button>
      </div>

      {/* Config Form */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border p-6 space-y-6">
        {/* Enable Toggle */}
        <div className="flex flex-col gap-3 p-4 bg-light-hover rounded-xl sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-medium text-light-text">启用AI提示词优化</h3>
            <p className="text-sm text-light-muted">开启后，用户可以在创作页面使用星星按钮优化提示词</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={form.isEnabled}
              onChange={(e) => setField('isEnabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
          </label>
        </div>

        {/* API Configuration */}
        <div>
          <label className="text-sm font-medium text-light-text mb-2 block">LLM 模型</label>
          <LlmModelPicker
            value={form.model}
            onChange={(id) => setField('model', id)}
          />
          <p className="text-xs text-light-muted mt-1">
            apiUrl / apiKey 已迁移到「LLM 平台 → 渠道」管理；此处仅选择对应模型实例。
          </p>
        </div>

        {/* Model Configuration */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-light-text">Temperature</label>
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={form.temperature}
              onChange={(e) => setField('temperature', parseFloat(e.target.value))}
              className="w-full px-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
            />
            <p className="text-xs text-light-muted">0-2之间，越高越有创意</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-light-text">最大Token数</label>
            <input
              type="number"
              min="100"
              max="4000"
              step="100"
              value={form.maxTokens}
              onChange={(e) => setField('maxTokens', parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20"
            />
          </div>
        </div>

        {/* Mode-specific System Prompts */}
        <div className="space-y-4 pt-4 border-t border-light-border">
          <h3 className="font-medium text-light-text flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-accent-blue" />
            模式专属系统提示词
          </h3>
          <p className="text-xs text-light-muted">为三种不同的生成模式配置专门的提示词优化策略</p>

          {/* Text2Img Prompt */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-light-text flex items-center gap-2">
              <FileText className="w-4 h-4 text-green-600" />
              文生图模式
            </label>
            <textarea
              value={form.systemPromptText2img}
              onChange={(e) => setField('systemPromptText2img', e.target.value)}
              rows={6}
              placeholder="输入文生图模式的系统提示词..."
              className="w-full px-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 resize-none"
            />
            <p className="text-xs text-light-muted">用于纯文本生成图片的场景，专注于将描述扩展为详细的画面提示词</p>
          </div>

          {/* Img2Img Prompt */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-light-text flex items-center gap-2">
              <ImagePlus className="w-4 h-4 text-blue-600" />
              参考图生图模式
            </label>
            <textarea
              value={form.systemPromptImg2img}
              onChange={(e) => setField('systemPromptImg2img', e.target.value)}
              rows={6}
              placeholder="输入参考图生图模式的系统提示词..."
              className="w-full px-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 resize-none"
            />
            <p className="text-xs text-light-muted">用于基于参考图生成新图片的场景，专注于理解修改意图和保留元素</p>
          </div>

          {/* Inpaint Prompt */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-light-text flex items-center gap-2">
              <Brush className="w-4 h-4 text-orange-600" />
              局部重绘模式
            </label>
            <textarea
              value={form.systemPromptInpaint}
              onChange={(e) => setField('systemPromptInpaint', e.target.value)}
              rows={6}
              placeholder="输入局部重绘模式的系统提示词..."
              className="w-full px-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 resize-none"
            />
            <p className="text-xs text-light-muted">用于局部涂抹重绘的场景，专注于描述蒙版区域内容和环境融合</p>
          </div>
        </div>

        {/* Merge Prompt Template */}
        <div className="space-y-4 pt-4 border-t border-light-border">
          <h3 className="font-medium text-light-text flex items-center gap-2">
            <Combine className="w-4 h-4 text-purple-600" />
            模板广场融合提示词模板
          </h3>
          <p className="text-xs text-light-muted">用于模板广场，定义如何将模板提示词和用户需求组合后发送给AI</p>

          <div className="space-y-2">
            <label className="text-sm font-medium text-light-text">融合提示词模板</label>
            <textarea
              value={form.mergePromptTemplate}
              onChange={(e) => setField('mergePromptTemplate', e.target.value)}
              rows={8}
              placeholder="输入融合提示词模板，使用 {{template}} 表示模板提示词，{{userRequirement}} 表示用户需求..."
              className="w-full px-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 resize-none"
            />
            <p className="text-xs text-light-muted">
              使用 {"{{template}}"} 表示模板提示词，{"{{userRequirement}}"} 表示用户需求。当模板没有设置独立的融合提示词时，将使用此公共模板。
            </p>
          </div>
        </div>

        {/* Save Success Message */}
        {saved && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-xl">
            <Check className="w-5 h-5" />
            <span>配置已保存</span>
          </div>
        )}
      </div>

      {/* Usage Tips */}
      <div className="bg-blue-50 rounded-2xl p-5 sm:p-6">
        <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
          <Wand2 className="w-5 h-5" />
          使用说明
        </h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>开启后，用户将在创作页面的提示词输入框旁看到星星按钮</li>
          <li>点击星星按钮，AI将实时流式输出优化后的提示词</li>
          <li>支持文生图、参考图生图、局部重绘三种模式的提示词优化</li>
          <li>三种模式使用不同的系统提示词，针对各自场景进行专门优化</li>
          <li>建议使用支持流式输出的OpenAI兼容API</li>
        </ul>
      </div>
    </div>
  );
}
